import React, { useEffect, useRef, useState } from "react";
import "./MultiSelect.css";

const ChevronIcon = () => (
  <svg
    className="al-ms__chevron"
    width="12"
    height="8"
    viewBox="0 0 12 8"
    fill="none"
    aria-hidden
  >
    <path
      d="M1 1.5L6 6.5L11 1.5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const getTriggerLabel = (value, placeholder) => {
  if (!value.length) return placeholder;
  if (value.length === 1) return value[0];
  return `Выбрано: ${value.length}`;
};

/** Выпадающий список с множественным выбором, поиском и чипами выбранных значений */
function MultiSelect({ label, options, value, onChange, placeholder = "Выберите" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    searchRef.current?.focus();

    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setIsOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setQuery("");
  }, [isOpen]);

  const normalizedQuery = query.trim().toLowerCase();
  const visibleOptions = normalizedQuery
    ? options.filter((option) => option.toLowerCase().includes(normalizedQuery))
    : options;
  const allVisibleSelected =
    visibleOptions.length > 0 && visibleOptions.every((option) => value.includes(option));

  const toggleOption = (option) => {
    onChange(
      value.includes(option)
        ? value.filter((item) => item !== option)
        : options.filter((item) => item === option || value.includes(item))
    );
  };

  const toggleVisible = () => {
    if (allVisibleSelected) {
      onChange(value.filter((item) => !visibleOptions.includes(item)));
    } else {
      onChange(options.filter((item) => value.includes(item) || visibleOptions.includes(item)));
    }
  };

  return (
    <div className="al-ms" ref={rootRef}>
      {label && <span className="al-ms__label">{label}</span>}
      <div className="al-ms__control">
      <button
        type="button"
        className={`al-ms__trigger${isOpen ? " is-open" : ""}${
          value.length ? " has-value" : ""
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className="al-ms__trigger-text">{getTriggerLabel(value, placeholder)}</span>
        <ChevronIcon />
      </button>

      {isOpen && (
        <div className="al-ms__popover">
          <input
            ref={searchRef}
            type="text"
            className="al-ms__search"
            placeholder="Поиск статьи..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="al-ms__actions">
            <button
              type="button"
              className="al-ms__action"
              disabled={!visibleOptions.length}
              onClick={toggleVisible}
            >
              {allVisibleSelected ? "Снять все" : "Выбрать все"}
            </button>
            {value.length > 0 && (
              <button type="button" className="al-ms__action" onClick={() => onChange([])}>
                Очистить
              </button>
            )}
          </div>
          <ul className="al-ms__list" role="listbox" aria-multiselectable="true">
            {visibleOptions.map((option) => {
              const checked = value.includes(option);
              return (
                <li key={option} role="option" aria-selected={checked}>
                  <label className={`al-ms__option${checked ? " is-checked" : ""}`}>
                    <input
                      type="checkbox"
                      className="oj-check"
                      checked={checked}
                      onChange={() => toggleOption(option)}
                    />
                    <span>{option}</span>
                  </label>
                </li>
              );
            })}
            {!visibleOptions.length && <li className="al-ms__empty">Ничего не найдено</li>}
          </ul>
        </div>
      )}
      </div>

      {value.length > 0 && (
        <div className="al-ms__chips">
          {value.map((item) => (
            <span key={item} className="al-ms__chip">
              {item}
              <button
                type="button"
                className="al-ms__chip-remove"
                aria-label={`Убрать «${item}»`}
                onClick={() => toggleOption(item)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default MultiSelect;
